import { useCallback } from "react";
import { useQuery } from "src/lib/react-query";
import { isTauriRuntime } from "src/lib/runtime/environment";

export type NativePdfDocument = {
  documentId: string;
  pageCount: number;
};

export type NativePdfFrame = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type NativePdfViewState = {
  viewId: string;
  documentId: string;
  currentPage: number;
  pageCount: number;
  scale: number;
};

export type NativePdfRenderedPage = {
  pageNumber: number;
  width: number;
  height: number;
  mimeType: string;
  imageBase64: string;
};

export type NativePdfSource =
  | { type: "url"; url: string; sourceId?: string }
  | { type: "path"; path: string; sourceId: string }
  | { type: "blob"; blob: Blob; sourceId: string }
  | { type: "bytes"; bytes: ArrayBuffer | Uint8Array; sourceId: string };

type NativePdfOpenDocumentInput = {
  sourceId: string;
  filePath?: string;
  url?: string;
};

type NativePdfMountViewInput = {
  viewId: string;
  documentId: string;
  frame: NativePdfFrame;
  scale?: number;
  rotation?: number;
  pageNumber?: number;
};

type NativePdfUpdateViewInput = {
  viewId: string;
  frame?: NativePdfFrame;
  scale?: number;
  rotation?: number;
  pageNumber?: number;
};

export class NativePdfUnsupportedError extends Error {
  constructor() {
    super("Native PDF rendering is only available in the desktop app");
    this.name = "NativePdfUnsupportedError";
  }
}

export class NativePdfFileSourceRequiredError extends Error {
  constructor() {
    super("Native PDF rendering requires a file path or a directly readable URL");
    this.name = "NativePdfFileSourceRequiredError";
  }
}

export const isNativePdfAvailable = (): boolean => isTauriRuntime();

export const nativePdfSourceId = (source: NativePdfSource): string => {
  switch (source.type) {
    case "url":
      return source.sourceId ?? source.url;
    case "path":
    case "blob":
    case "bytes":
      return source.sourceId;
  }
};

const canOpenNativePdfUrl = (url: string): boolean => {
  try {
    return ["file:", "http:", "https:"].includes(new URL(url).protocol);
  } catch {
    return false;
  }
};

export const canOpenNativePdfSource = (source: NativePdfSource | undefined): source is NativePdfSource =>
  source?.type === "path" || (source?.type === "url" && canOpenNativePdfUrl(source.url));

const sourceToOpenInput = (source: NativePdfSource): NativePdfOpenDocumentInput => {
  const sourceId = nativePdfSourceId(source);

  if (source.type === "path") {
    return {
      sourceId,
      filePath: source.path,
    };
  }

  if (source.type === "url" && canOpenNativePdfUrl(source.url)) {
    return {
      sourceId,
      url: source.url,
    };
  }

  throw new NativePdfFileSourceRequiredError();
};

/**
 * What PDFKit says when it is handed a document id it does not have open.
 *
 * A document id is a live handle into the desktop process, so this is what every caller holding a
 * stale one sees — see {@link renderNativePdfPage}, which repairs it.
 */
const MISSING_NATIVE_PDF_DOCUMENT_ERROR = "Native PDF document was not open";

export const isMissingNativePdfDocumentError = (error: unknown): boolean =>
  (error instanceof Error ? error.message : String(error ?? "")).includes(MISSING_NATIVE_PDF_DOCUMENT_ERROR);

const invokeNativePdf = async <Result>(command: string, input: unknown): Promise<Result> => {
  if (!isNativePdfAvailable()) {
    throw new NativePdfUnsupportedError();
  }

  const { invoke } = await import("@tauri-apps/api/core");
  try {
    return await invoke<Result>(command, { input });
  } catch (error) {
    // These commands fail with `Result<_, String>`, so Tauri rejects with a bare string. Everything
    // downstream — the error toast, which falls back to a generic "server had a hiccup" when there
    // is no `.message`, and every `instanceof Error` check — assumes an Error, so make it one here
    // rather than in each caller.
    throw error instanceof Error ? error : new Error(String(error));
  }
};

/**
 * How each open document was opened, so a handle can be re-made without its opener.
 *
 * PDFKit hands back an id, not a document; the id stops meaning anything the moment that document
 * is closed (or the app restarts). Whoever is *rendering* a page — a thumbnail deep in a rail — is
 * usually nowhere near whoever opened it, so recording the open input here is what lets the render
 * repair itself instead of failing at the far end of the app.
 */
const openDocumentInputs = new Map<string, NativePdfOpenDocumentInput>();

export const openNativePdfDocument = async (source: NativePdfSource): Promise<NativePdfDocument> => {
  const input = sourceToOpenInput(source);
  const document = await invokeNativePdf<NativePdfDocument>("native_pdf_open_document", input);
  openDocumentInputs.set(document.documentId, input);
  return document;
};

export const closeNativePdfDocument = async (documentId: string): Promise<void> => {
  openDocumentInputs.delete(documentId);
  await invokeNativePdf("native_pdf_close_document", { documentId });
};

export const mountNativePdfView = async (input: NativePdfMountViewInput): Promise<NativePdfViewState> => {
  return invokeNativePdf<NativePdfViewState>("native_pdf_mount_view", input);
};

export const updateNativePdfView = async (input: NativePdfUpdateViewInput): Promise<NativePdfViewState> => {
  return invokeNativePdf<NativePdfViewState>("native_pdf_update_view", input);
};

export const getNativePdfViewState = async (viewId: string): Promise<NativePdfViewState> => {
  return invokeNativePdf<NativePdfViewState>("native_pdf_get_view_state", { viewId });
};

export const showNativePdfFind = async (viewId: string): Promise<void> => {
  await invokeNativePdf("native_pdf_show_find", { viewId });
};

export const unmountNativePdfView = async (viewId: string): Promise<void> => {
  await invokeNativePdf("native_pdf_unmount_view", { viewId });
};

export const renderNativePdfPage = async ({
  documentId,
  pageNumber,
  targetWidth,
  rotation,
  region,
}: {
  documentId: string;
  pageNumber: number;
  targetWidth: number;
  rotation?: number;
  /** Normalized displayed-page coordinates with a top-left origin. */
  region?: NativePdfFrame;
}): Promise<NativePdfRenderedPage> => {
  const request = { documentId, pageNumber, targetWidth, rotation, region };
  try {
    return await invokeNativePdf<NativePdfRenderedPage>("native_pdf_render_page", request);
  } catch (error) {
    const openInput = openDocumentInputs.get(documentId);
    if (!openInput || !isMissingNativePdfDocumentError(error)) throw error;
    // The document was closed out from under this render. Reopening is keyed by the same source id,
    // so the handle comes back identical and nothing above here has to know it happened.
    await invokeNativePdf<NativePdfDocument>("native_pdf_open_document", openInput);
    return invokeNativePdf<NativePdfRenderedPage>("native_pdf_render_page", request);
  }
};

export function useNativePdfDocument({
  source,
  enabled = true,
}: {
  source?: NativePdfSource;
  enabled?: boolean;
}) {
  const sourceId = source ? nativePdfSourceId(source) : undefined;

  return useQuery({
    queryKey: ["native-pdf-document", sourceId],
    enabled: enabled && isNativePdfAvailable() && canOpenNativePdfSource(source),
    staleTime: Infinity,
    // NOT persisted, unlike every other query. A document id names a PDFKit handle inside the
    // desktop process; restoring one from IndexedDB on the next launch hands out an id that names
    // nothing, and `staleTime: Infinity` means it is never re-fetched — so every render against it
    // fails forever. The open is cheap; re-doing it each launch is the point.
    persister: undefined,
    queryFn: useCallback(async () => {
      if (!source) {
        throw new NativePdfUnsupportedError();
      }
      return openNativePdfDocument(source);
    }, [source]),
  });
}

export function useNativePdfPageImage({
  documentId,
  pageNumber,
  targetWidth,
  rotation,
  enabled = true,
}: {
  documentId?: string;
  pageNumber: number;
  targetWidth: number;
  rotation?: number;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["native-pdf-page-image", documentId, pageNumber, targetWidth, rotation ?? 0],
    enabled: enabled && isNativePdfAvailable() && !!documentId && targetWidth > 0,
    staleTime: Infinity,
    // Also unpersisted: these are base64 rasters of borrower pages keyed by a handle that does not
    // survive the process, and PDFKit already caches them on its own side of the boundary.
    persister: undefined,
    queryFn: useCallback(async () => {
      if (!documentId) {
        throw new NativePdfUnsupportedError();
      }
      return renderNativePdfPage({ documentId, pageNumber, targetWidth, rotation });
    }, [documentId, pageNumber, targetWidth, rotation]),
  });
}
